"use client";

import { useEffect } from "react";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { Icon } from "@/components/ui/Icon";
import { evaluatePassword, PASSWORD_RULES } from "@/lib/auth/password-rules";
import { usePwnedPassword, type PwnedStatus } from "@/lib/auth/pwned-password";

interface PasswordStrengthProps {
  password: string;
  /**
   * The re-enter field's value. Pass it and the checklist grows a "Both
   * passwords match" row. Without it the only feedback on the confirm field
   * is an error that appears after a failed submit, so the second box looks
   * like it does nothing while you type.
   */
  confirmPassword?: string;
  /** Optional id so the input can `aria-describedby` it. */
  id?: string;
  /**
   * Notified whenever the live breach-check status changes, so the parent form
   * can gate its submit button (via `pwnedBlocksSubmit`). Pass a stable setter.
   */
  onPwnedStatusChange?: (status: PwnedStatus) => void;
}

export function PasswordStrength({ password, confirmPassword, id, onPwnedStatusChange }: PasswordStrengthProps) {
  const result = evaluatePassword(password);
  // Hooks must run unconditionally — call before the empty-password early return.
  const pwnedStatus = usePwnedPassword(password);

  useEffect(() => {
    onPwnedStatusChange?.(pwnedStatus);
  }, [pwnedStatus, onPwnedStatusChange]);

  if (password.length === 0) return null;

  // The breach row only appears once the composition rules pass (before that
  // there's nothing to look up, and a lone red row would just be noise).
  //
  // `error` (HIBP unreachable) used to render as a green tick claiming "Not a
  // commonly breached password" — a claim we hadn't actually verified. That is
  // the one remaining way to get an all-green checklist and a server rejection:
  // leaked-password protection is ON in production, so a user whose browser
  // can't reach api.pwnedpasswords.com (corporate proxy, ad blocker, CSP) sails
  // through our checklist and gets bounced by Supabase. Submitting still fails
  // open — the server is the real backstop and a HIBP outage must never wall
  // off signup — but the row no longer says something we don't know.
  const showPwnedRow = pwnedStatus !== "idle";
  const pwnedMet = pwnedStatus === "safe";
  const pwnedChecking = pwnedStatus === "checking";
  const pwnedUnknown = pwnedStatus === "error";

  // Only once they've started re-entering — a red "don't match" row against an
  // empty second box would be nagging about something they haven't done yet.
  const showMatchRow = (confirmPassword ?? "").length > 0;
  const matched = confirmPassword === password;

  return (
    <ul id={id} className="mucka-pw-checks" aria-live="polite">
      {PASSWORD_RULES.map((rule) => {
        const met = result[rule.key];
        return (
          <li
            key={rule.key}
            className={`mucka-pw-check${met ? " mucka-pw-check--met" : ""}`}
            aria-label={`${rule.label} ${met ? "(met)" : "(not yet)"}`}
          >
            <span className="mucka-pw-check-dot" aria-hidden="true">
              <Icon
                icon={met ? Check : AlertTriangle}
                size={12}
                strokeWidth={met ? 3 : 2.5}
              />
            </span>
            <span className="mucka-pw-check-label t-body-sm" aria-hidden="true">
              {rule.label}
            </span>
          </li>
        );
      })}

      {showPwnedRow && (
        <li
          className={`mucka-pw-check${pwnedMet ? " mucka-pw-check--met" : ""}${
            pwnedStatus === "pwned" ? " mucka-pw-check--alert" : ""
          }${pwnedChecking ? " mucka-pw-check--checking" : ""}`}
          aria-label={
            pwnedChecking
              ? "Checking password against known breaches"
              : pwnedStatus === "pwned"
                ? "This password has appeared in a data breach"
                : pwnedUnknown
                  ? "Could not check this password against known breaches"
                  : "Not a commonly breached password (met)"
          }
        >
          <span className="mucka-pw-check-dot" aria-hidden="true">
            <Icon
              icon={pwnedChecking ? Loader2 : pwnedMet ? Check : AlertTriangle}
              size={12}
              strokeWidth={pwnedMet ? 3 : 2.5}
            />
          </span>
          <span className="mucka-pw-check-label t-body-sm" aria-hidden="true">
            {pwnedChecking
              ? "Checking it's not in a known breach…"
              : pwnedStatus === "pwned"
                ? "This one's turned up in a data breach — pick another"
                : pwnedUnknown
                  ? "Couldn't check this against known breaches — you can still carry on"
                  : "Not a commonly breached password"}
          </span>
        </li>
      )}

      {showMatchRow && (
        <li
          className={`mucka-pw-check${matched ? " mucka-pw-check--met" : ""}`}
          aria-label={matched ? "Both passwords match (met)" : "Both passwords match (not yet)"}
        >
          <span className="mucka-pw-check-dot" aria-hidden="true">
            <Icon
              icon={matched ? Check : AlertTriangle}
              size={12}
              strokeWidth={matched ? 3 : 2.5}
            />
          </span>
          <span className="mucka-pw-check-label t-body-sm" aria-hidden="true">
            {matched ? "Both passwords match" : "Both passwords match — not yet"}
          </span>
        </li>
      )}
    </ul>
  );
}
